import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

/**
 * COD Reconciliation — where every Cash-on-Delivery order's MONEY is, which is
 * a different question from where its goods are.
 *
 * One row = one COD order = one shipment = one delivery. The backend keys
 * everything by shipment id; there is no separate ledger table, so `id` here is
 * always a shipment id.
 *
 * Backend: `docs/cod-reconciliation/assessment-and-plan.md` in
 * nest-logistic-service.
 */

/**
 * The five buckets the list groups rows into. NOT the eight tiers the original
 * mockup drew: `escalated`, `deposit_overdue`, `needs_attention` and
 * `unreachable` all depend on an ageing threshold nobody has agreed, and the
 * one the PRD assumed (24h) is contradicted by production — the real median is
 * 31.1h, so it would paint over half the list red on day one. The list sorts by
 * elapsed time per row instead, which surfaces the same rows without inventing a
 * number.
 */
export const COD_BUCKETS = [
  'in_delivery',
  'outstanding',
  'pending_deposit',
  'pending_finance',
  'confirmed',
  // The two that are not steps along the money's journey sit at the end, in the
  // order a reader meets them: a delivery can still come back from
  // `failed_delivery`, never from `not_applicable`.
  'failed_delivery',
  'not_applicable',
] as const;
export type CodBucket = (typeof COD_BUCKETS)[number];

/** Tab / card labels, in money order. */
export const COD_BUCKET_LABELS: Record<CodBucket, string> = {
  // Named after WHO HOLDS THE CASH, which is what the other four cards say
  // too: customer → rider → PIC → Finance. That makes the custody chain
  // readable straight off the card row, and it is the one statement true of
  // every shipment in this bucket.
  //
  // Rejected alternatives, both of which are wrong for part of the bucket:
  // "Sedang Dikirim" and "Belum Dikirim" describe a DELIVERY stage, but
  // `cod_status IS NULL` spans CREATED, AT_HUB, DISPATCHED and IN_DELIVERY —
  // "belum dikirim" is false for the row currently out with a rider, and
  // "sedang dikirim" is false for the one still on the hub floor.
  // "Belum Tertagih" was accurate but said nothing about where the money is,
  // and on a screen whose subtitle is "posisi uang" that is the question.
  in_delivery: 'Uang di Customer',
  outstanding: 'Belum Disetor Rider',
  pending_deposit: 'Menunggu Deposit PIC',
  // The PIC says the money was transferred and attached proof, but nobody has
  // found it on a bank statement yet. Naming the step after what Finance still
  // owes keeps the card row readable as a queue of work.
  pending_finance: 'Menunggu Konfirmasi Finance',
  // Now means "the money is verifiably in the account", not "the PIC says they
  // deposited it". That is the whole point of Leg 3 — before it, this card was
  // a claim wearing the word Selesai.
  confirmed: 'Selesai',
  // Not folded into 'Retur / Gagal Kirim': that one is terminal, and this order
  // may still be retried and collected. Folding them would tell Ops an order is
  // closed while a rider is on the way back to it.
  failed_delivery: 'Gagal Kirim (bisa retry)',
  // Not "Tidak Berlaku": it read as a configuration state ("COD is not
  // enabled") rather than what it is — the delivery came back or failed, so
  // the money was never collected and never will be. That is 12.6% of
  // production COD orders, and the label has to say why they are here.
  not_applicable: 'Retur / Gagal Kirim',
};

/** One-line explanation of each bucket, for the card subtitle / tooltip. */
export const COD_BUCKET_HINTS: Record<CodBucket, string> = {
  in_delivery:
    'Barang belum diterima penerima (masih dibuat, di hub, sudah didispatch, atau sedang dikirim) — customer belum membayar, jadi belum jadi tanggungan rider maupun PIC',
  outstanding: 'Rider sudah menerima uang, belum diserahkan ke PIC',
  pending_deposit: 'PIC memegang uang, belum disetor ke Finance',
  pending_finance:
    'PIC sudah setor dan melampirkan bukti — Finance belum menemukannya di mutasi bank',
  confirmed: 'Sudah dicocokkan Finance dengan mutasi bank. Status akhir',
  failed_delivery:
    'Pengiriman gagal dan masih mungkin diulang — belum ada uang yang dikumpulkan. Kalau berhasil di percobaan berikutnya, otomatis kembali ke Belum Disetor Rider',
  not_applicable:
    'Pengiriman retur atau gagal — COD tidak pernah tertagih. Status akhir, tidak perlu tindakan',
};

/**
 * Who settled Leg 1. Not the same as "the leg closed": all three values move
 * the order to `pending_deposit`, but only the first means the rider actually
 * paid.
 */
export const COD_SETTLEMENT_SOURCES = [
  'rider_paid',
  'pic_covered',
  'tribe_write_off',
] as const;
export type CodSettlementSource = (typeof COD_SETTLEMENT_SOURCES)[number];

export const COD_SETTLEMENT_SOURCE_LABELS: Record<CodSettlementSource, string> =
  {
    rider_paid: 'Rider Paid',
    pic_covered: 'PIC Covered (Talangan)',
    tribe_write_off: 'Tribe Write-off',
  };

/**
 * What each source actually means, shown under the picker.
 *
 * All three move the order to `pending_deposit`, so without this the operator
 * cannot tell "the money was collected" from "the PIC is now personally out of
 * pocket" from "we lost it" — and those are the three things Finance most needs
 * distinguished.
 */
export const COD_SETTLEMENT_SOURCE_HINTS: Record<CodSettlementSource, string> =
  {
    rider_paid: 'Rider menyerahkan uang yang dia kumpulkan dari customer.',
    pic_covered:
      'Rider belum menyerahkan; PIC menutup pakai uang sendiri. Rider masih berutang ke PIC.',
    tribe_write_off:
      'Tidak ada yang membayar; tribe menyerap kerugiannya. Ini kejadian akuntansi, bukan penagihan.',
  };

/** Leg 2 deposit method. One value today; the backend still validates the set. */
export const COD_DEPOSIT_METHODS = ['bank_transfer'] as const;
export type CodDepositMethod = (typeof COD_DEPOSIT_METHODS)[number];

export const COD_DEPOSIT_METHOD_LABELS: Record<CodDepositMethod, string> = {
  bank_transfer: 'Bank Transfer',
};

export type CodLegType = 'rider_to_pic' | 'pic_to_finance';

/** The rider who carried the goods, frozen at the time they carried them. */
export interface CodOrderRider {
  id: number;
  code: string;
  name: string;
  phone: string | null;
}

/** One stat card: how many orders, and how much money. */
export interface CodBucketTotal {
  count: number;
  amount: number;
}

/**
 * The stat cards. Counted across EVERY bucket under the same scope filters as
 * the rows — never narrowed by the selected tab, or the cards would just
 * restate the table. `total` always equals the sum of the five.
 */
export interface CodOrderSummary {
  total: CodBucketTotal;
  inDelivery: CodBucketTotal;
  outstanding: CodBucketTotal;
  pendingDeposit: CodBucketTotal;
  pendingFinance: CodBucketTotal;
  confirmed: CodBucketTotal;
  failedDelivery: CodBucketTotal;
  notApplicable: CodBucketTotal;
}

/** Maps a bucket to its key on the summary object. */
export const COD_SUMMARY_KEYS: Record<CodBucket, keyof CodOrderSummary> = {
  in_delivery: 'inDelivery',
  outstanding: 'outstanding',
  pending_deposit: 'pendingDeposit',
  pending_finance: 'pendingFinance',
  confirmed: 'confirmed',
  failed_delivery: 'failedDelivery',
  not_applicable: 'notApplicable',
};

export interface CodOrderRow {
  /** Shipment id — the key every other endpoint here takes. */
  id: string;
  bookingId: string;
  deliveryUid: string | null;
  /** driver-service rider id — what the back-office driver page is keyed by.
   *  Null when the snapshot predates the rider-snapshot column. */
  riderId: number | null;
  riderName: string | null;
  riderCode: string | null;
  /** From the frozen snapshot. Drives the row's WhatsApp action. */
  riderPhone: string | null;
  clientName: string | null;
  hubName: string | null;
  /** Accountable PIC: the email of whoever inbound-scanned the goods first. */
  owner: string | null;
  amount: number;
  paymentMethod: string | null;
  /**
   * Null means the money is not yet anyone's responsibility — the
   * "Uang di Customer" bucket. Otherwise one of the four stored COD statuses.
   */
  status: string | null;
  /** When the current custody stage began; "Time in Status" is derived from it. */
  stageStartedAt: string | null;
  /**
   * Kapan pengirimannya SELESAI — momen uang jadi tanggungan seseorang.
   *
   * Bukan `stageStartedAt`, yang di-arm ulang tiap tahap dan karenanya hanya
   * berarti "tanggal selesai" selama baris masih di bucket pertama. Null untuk
   * pesanan yang belum selesai, yang retur/gagal kirim, dan yang selesai
   * sebelum jejak auditnya ada.
   */
  completedAt: string | null;
  hasDiscrepancy: boolean;
  /** Leg 1's settlement source. Null until Leg 1 closes — all three values
   *  land on `pending_deposit`, so the bucket alone cannot tell them apart. */
  settlementSource: CodSettlementSource | null;
  /** Leg 2's deposit method. Its presence IS "this was deposited". */
  depositMethod: string | null;
  /** When the money reached Finance. Null until Leg 2 closes. */
  resolvedAt: string | null;
  /**
   * Signed discrepancy per leg, and proof presence per leg. Null = that leg
   * has not closed. Per leg because the baselines differ — a PIC depositing
   * short is not the rider handing over short, and one merged flag showed the
   * former against the rider's column.
   */
  discrepancyLeg1: number | null;
  discrepancyLeg2: number | null;
  /** What reached Finance — Leg 2's confirmed amount. Null until Leg 2 closes.
   *  Against `amount` this is the end-to-end result for the whole order. */
  depositedAmount: number | null;
  leg1HasProof: boolean | null;
  leg2HasProof: boolean | null;
  /** Who absorbs each leg's gap. Null when the leg reconciled or is not closed. */
  discrepancyTreatmentLeg1: string | null;
  discrepancyTreatmentLeg2: string | null;
  /**
   * Leg 3 — Finance's verdict. `financeConfirmedAt` is the real "money
   * verified" moment; `resolvedAt` only says when the PIC claimed to bank it.
   */
  financeBankRef: string | null;
  financeConfirmedBy: string | null;
  financeConfirmedAt: string | null;
  /**
   * True when every closed leg carries a proof file — and true when nothing is
   * closed yet, because there is nothing to evidence.
   *
   * Proof is optional for both legs, so a confirmed row can be unevidenced —
   * this is the only thing that records whether a document exists.
   */
  hasProof: boolean;
}

/**
 * Columns the list may be ordered by, server-side. Mirrors `CodSortBy` in the
 * BE — the value reaches an ORDER BY there, so it is a closed set on both ends.
 */
export const COD_SORT_FIELDS = [
  'bookingId',
  'amount',
  'stageStartedAt',
  'completedAt',
  'status',
  'rider',
] as const;
export type CodSortField = (typeof COD_SORT_FIELDS)[number];
export type CodSortDir = 'asc' | 'desc';

export interface CodOrderListParams {
  clientId?: number;
  bucket?: CodBucket;
  /** Server-side ordering. Sorting a paginated list in the browser answers
   *  "the largest on this page", which is not the question. */
  sortBy?: CodSortField;
  sortDir?: CodSortDir;
  /** One rider, by driver-service id. The bulk modal's caller: it asks for
   *  exactly this rider's unsettled orders. */
  riderId?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  /** Rentang tanggal SELESAI. Berdiri sendiri dari pasangan di atas. */
  completedStartDate?: string;
  completedEndDate?: string;
  page?: number;
  limit?: number;
}

/**
 * Rows AND cards in one response, deliberately. The cards count across every
 * bucket while the rows show one, so two endpoints would let the UI pair a card
 * from one moment with a page from another — and "the tab says 4, the card says
 * 5" is exactly the class of bug this feature cannot afford, since both numbers
 * are money.
 */
export interface CodOrderListResult {
  summary: CodOrderSummary;
  rows: CodOrderRow[];
  total: number;
  page: number;
  limit: number;
}

/**
 * One custody handoff.
 *
 * `baselineAmount` is what THIS leg was measured against, and it is not the
 * same figure for both: Leg 1 compares to the original COD amount (did the
 * rider hand over what the customer paid), Leg 2 compares to Leg 1's confirmed
 * amount (did the PIC bank what the rider gave them). The backend resolves it
 * per leg so the UI never has to re-derive it — a rider paying short and a PIC
 * depositing short are different failures with different people to ask.
 */
/** What an attachment IS, from the operator's point of view. */
export const COD_PROOF_TYPES = [
  'bukti_transfer',
  'berita_acara',
  'lainnya',
] as const;
export type CodProofType = (typeof COD_PROOF_TYPES)[number];

export const COD_PROOF_TYPE_LABELS: Record<CodProofType, string> = {
  bukti_transfer: 'Bukti Transfer',
  berita_acara: 'Berita Acara',
  lainnya: 'Dokumen Lainnya',
};

/**
 * One attachment on a leg.
 *
 * Two fields only. Who attached it and when lives on the `proof_added` /
 * `correction` timeline entry (`changedBy` / `changedAt`), not inside the
 * attachment — one answer to that question instead of two that can disagree.
 */
export interface CodConfirmationProof {
  imageUrl: string;
  type: CodProofType;
}

export interface CodOrderLeg {
  id: string;
  /** Leg 1: settlement source. Leg 2: deposit method. */
  method: string | null;
  baselineAmount: number;
  confirmedAmount: number;
  /** Negative means short against this leg's baseline. */
  discrepancyAmount: number;
  discrepancyNote: string | null;
  /** Attachments, oldest first. EMPTY is what marks a leg unevidenced —
   *  optional on Leg 1, mandatory on Leg 2. */
  proofFiles: CodConfirmationProof[];
  notes: string | null;
  /** The rider as at the moment this leg closed. Leg 2 has none. */
  rider: CodOrderRider | null;
  confirmedBy: string;
  confirmedAt: string;
  /** Non-null only when the row was corrected after the fact. */
  updatedBy: string | null;
  updatedAt: string | null;
}

/**
 * What an entry is about, in fields rather than prose.
 *
 * Started as "the values a correction overwrote" and is now every event's
 * frozen facts, for one reason: the timeline renders structured fields and
 * never the audit `note`, so anything only the note carried — a flag's reason,
 * the operator's own words, the bank reference — never reached the screen.
 */
export interface CodHistorySnapshot {
  legType?: string | null;
  method?: string | null;
  confirmedAmount?: string | null;
  discrepancyAmount?: string | null;
  discrepancyNote?: string | null;
  /** Historical: entries written before the proof list existed. */
  proofFile?: string | null;
  /**
   * On a `correction`, the attachments as they stood BEFORE the edit — which is
   * what makes a removed one visible after the fact. On a `proof_added`, the
   * ones that were added.
   */
  proofFiles?: CodConfirmationProof[] | null;
  notes?: string | null;
  /** `finance_flagged` / `finance_returned`: Finance's verdict, frozen. The
   *  live columns are transient — `clear` nulls the reason, and a returned
   *  deposit's row is retired when the PIC deposits again. */
  financeIssueReason?: CodFinanceIssueReason | null;
  financeIssueNote?: string | null;
  /** `finance_matched` / `confirm_leg_3`: the bank statement line, frozen. */
  financeBankRef?: string | null;
}

export type CodHistoryEventType =
  | 'status_change'
  | 'confirm_leg_1'
  | 'confirm_leg_2'
  | 'correction'
  /** Evidence attached to a leg that had already closed. */
  | 'proof_added'
  /** Finance tied the deposit to a bank statement line. */
  | 'finance_matched'
  /** Leg 3 — Finance verified the money arrived. Terminal. */
  | 'confirm_leg_3'
  /** Flagged as problematic and held; the order stays in Finance's queue. */
  | 'finance_flagged'
  /** Sent back to the PIC to deposit again; the old deposit is retired. */
  | 'finance_returned'
  /** A PIC opened a WhatsApp follow-up. Records the reach-out, not a reply. */
  | 'rider_contacted';

/** One entry in the COD audit trail. Newest first. */
export interface CodOrderHistoryEntry {
  id: string;
  eventType: CodHistoryEventType;
  fromStatus: string | null;
  toStatus: string | null;
  /** `correction` entries only: the figures before the edit. */
  snapshot: CodHistorySnapshot | null;
  note: string | null;
  /** An email, or `SYSTEM` for a transition the delivery listener made. */
  changedBy: string;
  changedAt: string;
}

export interface CodOrderDetail {
  id: string;
  bookingId: string;
  waybill: string;
  deliveryUid: string | null;
  clientName: string | null;
  hubName: string | null;
  owner: string | null;
  rider: CodOrderRider | null;
  amount: number;
  paymentMethod: string | null;
  status: string | null;
  stageStartedAt: string | null;
  /**
   * Kapan pengirimannya SELESAI — momen uang jadi tanggungan seseorang.
   *
   * Bukan `stageStartedAt`, yang di-arm ulang tiap tahap dan karenanya hanya
   * berarti "tanggal selesai" selama baris masih di bucket pertama. Null untuk
   * pesanan yang belum selesai, yang retur/gagal kirim, dan yang selesai
   * sebelum jejak auditnya ada.
   */
  completedAt: string | null;
  createdAt: string;
  legRiderToPic: CodOrderLeg | null;
  legPicToFinance: CodOrderLeg | null;
  timeline: CodOrderHistoryEntry[];
}

export const getCodOrders = async (
  params: CodOrderListParams
): Promise<CodOrderListResult> => {
  try {
    const res = await Axios.get('/v1/cod-orders', { params });
    return res.data.data as CodOrderListResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getCodOrderDetail = async (
  id: string
): Promise<CodOrderDetail> => {
  try {
    const res = await Axios.get(`/v1/cod-orders/${id}`);
    return res.data.data as CodOrderDetail;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Upload a proof document and get back its permanent GCS url.
 *
 * Two-step on purpose: the confirm endpoints take a URL, not bytes, so a
 * rolled-back confirmation can never strand an uploaded object. It also means
 * the confirm request is plain JSON with a validated DTO behind it.
 *
 * ⚠️ `/v1/uploads/main` DOES NOT EXIST YET — it is task D13, on Joshua's plate
 * (spec in nest-logistic-service TODOS.md). It is pointed here rather than at
 * the existing `/v1/invoices/uploads/main` because that one cannot serve this:
 * `invoiceId` is a hard requirement there and is used as the storage folder, so
 * a COD proof would either be rejected (verified: `400 invoiceId is required`)
 * or filed under a made-up invoice id. Pointing at the real target means this
 * starts working the moment D13 ships, with no change here.
 *
 * `Content-Type: undefined` is load-bearing. The shared Axios instance sets a
 * default `application/json`, and axios only computes a multipart boundary when
 * no Content-Type is already set — leave the default in place and the server
 * sees JSON, `isMultipart()` is false, and the body serialises to `{}`.
 */
/**
 * What the file is for. The server uses it to pick the GCS folder, and it is a
 * closed set there — an open `folder` field would let a caller write anywhere
 * in the bucket, including on top of the invoice namespace.
 *
 * Must match `CodUploadType` in the service exactly — it is validated there,
 * not coerced.
 */
const COD_UPLOAD_TYPE = 'COD_RECONCILIATION';

/**
 * Upload a proof and get its URL back.
 *
 * Straight to `main`, never `temp`: a proof is attached at the moment of
 * confirming, so there is no draft step to stage it through, and a file left in
 * a temp bucket would be a file the confirm row points at and nobody cleans up.
 *
 * NOT `utils/uploadApi.ts`. That helper exists but targets core-service
 * (`POST /v2/upload/image` then `PUT /v1/upload/temp` — a two-step signed-URL
 * flow), routes this service does not serve, and nothing in this app calls it.
 * COD proofs live in logistic's bucket beside the ledger that references them.
 *
 * The confirm endpoints take this URL rather than the bytes — deliberately, so
 * a rolled-back confirm transaction cannot strand a half-written object.
 */
export const uploadCodProof = async (file: File): Promise<string> => {
  const form = new FormData();
  form.append('type', COD_UPLOAD_TYPE);
  form.append('file', file);
  try {
    // `Content-Type: undefined` so the browser sets its own multipart boundary.
    // Axios' JSON default would otherwise win and the server would read no
    // parts at all.
    const res = await Axios.post('/v1/cod-orders/uploads/main', form, {
      headers: { 'Content-Type': undefined },
    });
    const url = res.data?.data?.imageUrl as string | undefined;
    if (!url) throw new Error('Upload berhasil tapi tidak mengembalikan url');
    return url;
  } catch (error) {
    // A 404/405 means the endpoint is not deployed to this environment yet.
    // Say that, rather than letting a raw "Request failed with status code 404"
    // reach an operator who cannot interpret it — and say the confirm still
    // works, because proof is optional.
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404 || status === 405) {
      throw new Error(
        'Fitur upload bukti belum tersedia di server ini. Konfirmasi tetap bisa disimpan tanpa bukti.'
      );
    }
    throw new Error(getBackendErrorMessage(error));
  }
};

export interface ConfirmCodLeg1Payload {
  settlementSource: CodSettlementSource;
  /**
   * Decimal string, NOT a number. `numeric(14,2)` all the way down, and the
   * backend rejects anything that is not `\d+(\.\d{1,2})?` rather than
   * reinterpreting it — `parseFloat('2.220.700')` is 2.22, which is a plausible
   * figure, not an error, so a thousands-separated value would otherwise be
   * stored silently wrong.
   */
  confirmedAmount: string;
  /** Required by the server whenever the amount differs from the baseline. */
  discrepancyNote?: string | null;
  notes?: string | null;
  /** Attachments, already uploaded via `uploadCodProof`. Optional on Leg 1,
   *  required by the server on Leg 2. */
  proofFiles?: CodConfirmationProof[];
}

export interface ConfirmCodLegResult {
  id: string;
  shipmentId: string;
  codStatus: string;
  codStageStartedAt: string;
  baselineAmount: number;
  discrepancyAmount: number;
  discrepancyNote: string | null;
  proofFiles: CodConfirmationProof[];
  notes: string | null;
  confirmedBy: string;
  confirmedAt: string;
}

export interface ConfirmCodLeg1Result extends ConfirmCodLegResult {
  settlementSource: string;
  confirmedAmount: number;
  /**
   * Whether the matching decrement reached delivery-service. False while the
   * `ENABLE_COD_SYNC_TO_DELIVERY` flag is off — the confirmation still stands
   * in logistic, but delivery-service's rider aggregate has not been reduced.
   */
  syncedToDeliveryService: boolean;
}

export interface ConfirmCodLeg2Result extends ConfirmCodLegResult {
  depositMethod: string;
  depositedAmount: number;
}

/** Leg 1 — the rider handed the cash to the PIC. */
export const confirmCodLeg1 = async (
  id: string,
  payload: ConfirmCodLeg1Payload
): Promise<ConfirmCodLeg1Result> => {
  try {
    const res = await Axios.patch(`/v1/cod-orders/${id}/confirm`, {
      settlementSource: payload.settlementSource,
      confirmedAmount: payload.confirmedAmount,
      discrepancyNote: payload.discrepancyNote || undefined,
      notes: payload.notes || undefined,
      proofFiles: payload.proofFiles,
    });
    return res.data.data as ConfirmCodLeg1Result;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export interface ConfirmCodLeg2Payload {
  depositMethod: CodDepositMethod;
  /** Decimal string — see ConfirmCodLeg1Payload.confirmedAmount. */
  depositedAmount: string;
  discrepancyNote?: string | null;
  notes?: string | null;
  proofFiles?: CodConfirmationProof[];
  /**
   * Who absorbs this order's Leg 1 shortfall.
   *
   * Required by the server exactly when Leg 1 closed short, and REJECTED when
   * it closed clean — send null in that case, not a default. Same rule the bulk
   * deposit endpoint applies (§7).
   */
  shortfallCoveredBy?: CodSettlementSource | null;
}

/** Leg 2 — the PIC banked the cash to Finance. Terminal. */
export const confirmCodLeg2 = async (
  id: string,
  payload: ConfirmCodLeg2Payload
): Promise<ConfirmCodLeg2Result> => {
  try {
    const res = await Axios.patch(`/v1/cod-orders/${id}/confirm-deposit`, {
      depositMethod: payload.depositMethod,
      depositedAmount: payload.depositedAmount,
      discrepancyNote: payload.discrepancyNote || undefined,
      notes: payload.notes || undefined,
      proofFiles: payload.proofFiles,
      shortfallCoveredBy: payload.shortfallCoveredBy || undefined,
    });
    return res.data.data as ConfirmCodLeg2Result;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export interface CorrectCodConfirmationPayload {
  /** Leg 1: a settlement source. Leg 2: a deposit method. */
  method: string;
  /** Decimal string — see ConfirmCodLeg1Payload.confirmedAmount. */
  confirmedAmount: string;
  discrepancyNote?: string | null;
  notes?: string | null;
  /** Required. What made the previous figures wrong. */
  reason: string;
  /** GCS url. Omitted leaves the original proof in place. */
  proofFiles?: CodConfirmationProof[];
}

export interface CorrectedCodLeg {
  id: string;
  legType: CodLegType;
  method: string | null;
  baselineAmount: number;
  confirmedAmount: number;
  discrepancyAmount: number;
  discrepancyNote: string | null;
  proofFiles: CodConfirmationProof[];
  notes: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

/**
 * Attach evidence to a leg that already closed — the `+ Tambah bukti` button.
 *
 * Add-only, and it keeps working after Finance has confirmed, which is when it
 * is actually needed: a dispute weeks later. Correction is locked by then, and
 * it should be — nothing about the figures is being restated.
 *
 * A url already attached is a no-op success, not an error. `added` says how
 * many were actually new.
 */
export interface AppendCodProofsResult {
  shipmentId: string;
  legType: string;
  proofFiles: CodConfirmationProof[];
  added: number;
  appendedBy: string;
  appendedAt: string;
}

export const appendCodProofs = async (
  id: string,
  legType: CodLegType,
  proofFiles: CodConfirmationProof[]
): Promise<AppendCodProofsResult> => {
  try {
    const res = await Axios.patch(
      `/v1/cod-orders/${id}/confirmations/${legType}/proofs`,
      { proofFiles }
    );
    return res.data.data as AppendCodProofsResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export interface CorrectCodConfirmationResult {
  shipmentId: string;
  corrected: CorrectedCodLeg;
  /**
   * Leg 2, when correcting Leg 1 forced it to be recomputed — Leg 2's baseline
   * IS Leg 1's confirmed amount. Null otherwise.
   */
}

/**
 * Correct the figures on a confirmation already recorded. Never changes the
 * order's status — it restates what happened at a leg, not whether it happened.
 * The superseded values are preserved in the timeline's `correction` entry.
 */
export const correctCodConfirmation = async (
  id: string,
  legType: CodLegType,
  payload: CorrectCodConfirmationPayload
): Promise<CorrectCodConfirmationResult> => {
  try {
    const res = await Axios.patch(
      `/v1/cod-orders/${id}/confirmations/${legType}`,
      {
        method: payload.method,
        confirmedAmount: payload.confirmedAmount,
        reason: payload.reason,
        discrepancyNote: payload.discrepancyNote || undefined,
        notes: payload.notes || undefined,
        proofFiles: payload.proofFiles,
      }
    );
    return res.data.data as CorrectCodConfirmationResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * One delivery's detail page — IN THIS APP, not back-office.
 *
 * logistic-web has its own `/delivery/:id` route keyed by the same delivery uid
 * back-office used, so the path shape is identical and only the origin changes.
 * Staying in-app means no env base to be missing and no second login.
 *
 * Still opened in a new tab by every caller: the COD drawer holds a half-read
 * reconciliation, and following a delivery should not be what closes it.
 */
export const deliveryDetailUrl = (deliveryUid: string | null): string | null =>
  deliveryUid ? `/delivery/${encodeURIComponent(deliveryUid)}` : null;

/**
 * One shipment's DETAIL panel on the Shipments page, open on arrival.
 *
 * The COD ledger is keyed by shipment id — a COD order has no id of its own —
 * so the id needed to address the panel is already on every row and every
 * detail response. That makes the search fallback unnecessary here: `?q=` lands
 * the operator on a filtered list they then have to click into, and the click
 * is a step this link can simply skip.
 *
 * Reads by `?detail=` on the Shipments page, which is push-history there, so
 * Back closes the panel rather than leaving the page.
 */
export const shipmentDetailUrl = (shipmentId: string | null): string | null =>
  shipmentId ? `/shipments?detail=${encodeURIComponent(shipmentId)}` : null;

/**
 * Back-office page for a rider.
 *
 * Keyed by the numeric rider id, not the `MTR…` code: the code is what a human
 * recognises, the id is what the route resolves. A row whose snapshot carried
 * no id therefore renders as plain text rather than a link that 404s.
 */
export const backOfficeDriverUrl = (riderId: number | null): string | null => {
  if (riderId == null) return null;
  const base = (process.env.REACT_APP_BACK_OFFICE_URL ?? '').replace(
    /\/+$/,
    ''
  );
  if (!base) return null;
  return `${base}/driver/${riderId}`;
};

/**
 * `wa.me` link for chasing a rider about cash they are still holding.
 *
 * Indonesian mobile numbers arrive in several shapes (`08…`, `+62…`, `62…`,
 * with spaces or dashes); `wa.me` accepts digits with a country code and
 * nothing else, so a raw `08…` silently opens an invalid chat. Normalised here
 * rather than at each call site.
 */
export const riderWhatsAppUrl = (phone: string | null): string | null => {
  if (!phone) return null;
  let digits = phone.replace(/[^\d]/g, '');
  if (!digits) return null;
  if (digits.startsWith('0')) digits = `62${digits.slice(1)}`;
  else if (!digits.startsWith('62')) digits = `62${digits}`;
  // 62 + at least 8 digits; anything shorter is not a reachable number.
  if (digits.length < 10) return null;
  return `https://wa.me/${digits}`;
};

/**
 * Any `method` value → its human label, whichever leg it came from.
 *
 * One lookup rather than two because the caller frequently does not know the
 * leg — a timeline entry carries a method but not always which vocabulary it
 * belongs to. Falls back to the raw value so a method added server-side shows
 * up as itself instead of vanishing, which is the failure that hides new data.
 */
export const codMethodLabel = (method: string | null | undefined): string => {
  if (!method) return '—';
  return (
    COD_SETTLEMENT_SOURCE_LABELS[method as CodSettlementSource] ??
    COD_DEPOSIT_METHOD_LABELS[method as CodDepositMethod] ??
    method
  );
};

/** `rider_to_pic` → `Leg 1 · Rider → PIC`. Falls back to the raw value. */
export const codLegLabel = (legType: string | null | undefined): string => {
  if (legType === 'rider_to_pic') return 'Leg 1 · Rider → PIC';
  if (legType === 'pic_to_finance') return 'Leg 2 · PIC → Finance';
  return legType ?? '—';
};

/**
 * Whether a proof url can be shown inline as an image.
 *
 * Extension-based on purpose: the url is a public GCS object and a HEAD request
 * just to learn its content-type would cost a round trip per row. A PDF gets a
 * link instead of a broken <img>, which is the only distinction that matters.
 */
export const isPreviewableImage = (url: string | null): boolean => {
  if (!url) return false;
  const path = url.split('?')[0].toLowerCase();
  return /\.(png|jpe?g|webp|gif|avif)$/.test(path);
};

/* ------------------------------------------------------------------ *
 * Bulk Reconciliation — the same ledger, grouped by the rider holding *
 * the cash, with one confirm per custody stage.                       *
 * ------------------------------------------------------------------ */

/** One rider group in the bulk view. */
export interface CodReconRiderRow {
  /** Null is the "no rider" group — shipments whose snapshot was never
   *  written. It is the signal that bulk is impossible for the group. */
  riderId: number | null;
  riderCode: string | null;
  riderName: string | null;
  riderPhone: string | null;
  /** Leg 1 pending — cash still in the rider's pocket. */
  leg1Count: number;
  leg1Amount: number;
  /** Leg 2 pending — cash with the PIC, not yet banked. */
  leg2Count: number;
  leg2Amount: number;
  /** Oldest stage start across BOTH stages: how long the oldest rupiah in
   *  this group has been out. */
  oldestStageStartedAt: string | null;
  /**
   * What this rider still owes from Leg 1 shortfalls charged to them. Signed;
   * negative means short.
   *
   * Spans EVERY order they carried, not just the unsettled ones counted above —
   * a debt does not disappear when its order reaches Selesai. So a rider can
   * show zero holdings and a non-zero figure here, and that combination is the
   * one worth chasing.
   */
  receivableAmount: number;
  receivableOrders: number;
}

export interface CodReconRidersResult {
  data: CodReconRiderRow[];
  totalRiders: number;
  totalLeg1Amount: number;
  totalLeg2Amount: number;
  /** Total still owed by riders. Not a subtotal of the two above — it spans
   *  settled orders too. */
  totalReceivableAmount: number;
}

export interface CodReconRidersParams {
  clientId?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  /** Completion-date range — same pair the board sends. */
  completedStartDate?: string;
  completedEndDate?: string;
}

/**
 * Not paginated, and that is deliberate on the server side: the view is bounded
 * by the number of riders holding unsettled cash, and its totals are meant to
 * be checked against the board's Outstanding + Pending Deposit cards.
 */
export const getCodReconRiders = async (
  params: CodReconRidersParams
): Promise<CodReconRidersResult> => {
  try {
    const res = await Axios.get('/v1/cod-orders/riders', { params });
    return res.data.data as CodReconRidersResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** One order the server refused to include, and why. */
export interface BulkConfirmIneligible {
  shipmentId: string;
  bookingId: string | null;
  reason: 'NOT_FOUND' | 'WRONG_STATUS' | 'ALREADY_CONFIRMED' | string;
  message: string;
}

export interface BulkConfirmResult {
  confirmed: string[];
  totalConfirmedAmount: number;
  bulkId: string;
}

export interface BulkConfirmCodPayload {
  shipmentIds: string[];
  settlementSource: CodSettlementSource;
  /** Decimal string. Must equal the sum of the members' COD amounts exactly —
   *  bulk has no discrepancy path, so the modal pre-fills and locks it. */
  receivedAmount: string;
  /** ONE set for the whole batch — the rider hands over once. */
  proofFiles?: CodConfirmationProof[];
  notes?: string | null;
}

/** Leg 1 for several orders from one rider, one shared proof. */
export const bulkConfirmCod = async (
  payload: BulkConfirmCodPayload
): Promise<BulkConfirmResult> => {
  try {
    const res = await Axios.patch('/v1/cod-orders/bulk-confirm', {
      shipmentIds: payload.shipmentIds,
      settlementSource: payload.settlementSource,
      receivedAmount: payload.receivedAmount,
      proofFiles: payload.proofFiles,
      notes: payload.notes || undefined,
    });
    return res.data.data as BulkConfirmResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** What the server actually wrote per order — the §7 arithmetic, stated. */
export interface BulkConfirmDepositMember {
  shipmentId: string;
  bookingId: string;
  codAmount: number;
  leg1ConfirmedAmount: number;
  leg1DiscrepancyAmount: number;
  depositedAmount: number;
  discrepancyAmount: number;
}

export interface BulkConfirmDepositResult extends BulkConfirmResult {
  members: BulkConfirmDepositMember[];
}

export interface BulkConfirmCodDepositPayload {
  shipmentIds: string[];
  depositMethod: CodDepositMethod;
  depositedAmount: string;
  /** Required when any member had a Leg 1 shortfall, rejected when none did.
   *  It changes the required total — see `requiredDepositTotal`. */
  shortfallCoveredBy?: CodSettlementSource | null;
  /** ONE set for the whole batch. Required — Leg 2 needs evidence. */
  proofFiles?: CodConfirmationProof[];
  notes?: string | null;
}

/** Leg 2 for several orders at once. */
export const bulkConfirmCodDeposit = async (
  payload: BulkConfirmCodDepositPayload
): Promise<BulkConfirmDepositResult> => {
  try {
    const res = await Axios.patch('/v1/cod-orders/bulk-confirm-deposit', {
      shipmentIds: payload.shipmentIds,
      depositMethod: payload.depositMethod,
      depositedAmount: payload.depositedAmount,
      shortfallCoveredBy: payload.shortfallCoveredBy || undefined,
      proofFiles: payload.proofFiles,
      notes: payload.notes || undefined,
    });
    return res.data.data as BulkConfirmDepositResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * What the PIC must deposit for a Leg 2 selection — mirrors the server's rule
 * so the modal can pre-fill it, and so the operator sees the figure change when
 * they pick who covers a shortfall.
 *
 *   rider_paid | pic_covered  → the ORIGINAL total. Someone makes the order
 *                               whole, so it nets to zero end to end.
 *   tribe_write_off           → the LEG 1 total, i.e. only what the PIC
 *                               actually holds. The order stays short on
 *                               purpose; that is what a write-off means.
 *
 * Kept in sync with `BulkConfirmCodDepositUseCase.plan()`. The server is the
 * authority — this exists so the operator is never shown a number the server
 * will then reject.
 */
export const requiredDepositTotal = (
  rows: Pick<CodOrderRow, 'amount' | 'discrepancyLeg1'>[],
  shortfallCoveredBy: CodSettlementSource | null
): number =>
  rows.reduce((total, row) => {
    const leg1 = row.amount + (row.discrepancyLeg1 ?? 0);
    return (
      total + (shortfallCoveredBy === 'tribe_write_off' ? leg1 : row.amount)
    );
  }, 0);

/** Whether a Leg 2 selection contains any order whose Leg 1 was not exact. */
export const hasLeg1Shortfall = (
  rows: Pick<CodOrderRow, 'discrepancyLeg1'>[]
): boolean => rows.some((r) => (r.discrepancyLeg1 ?? 0) !== 0);

/* ────────────────────────── Leg 3 — Finance verification ──────────────────── */

/**
 * Why Finance could not confirm a deposit. Mirrors `CodFinanceIssueReason`.
 *
 * `not_in_mutation` leads the list because it is both the commonest and the most
 * benign: inter-bank transfers settle late, so a Friday evening deposit may not
 * appear until Monday. It is a hold, not a rejection.
 */
/** Who absorbs a discrepancy. Mirrors `CodDiscrepancyTreatment`. */
export const COD_DISCREPANCY_TREATMENT_LABELS: Record<string, string> = {
  charge_rider: 'Tagih ke Rider',
  pic_cover: 'Talangan PIC',
  write_off: 'Write-off (Tribe)',
};

export const COD_FINANCE_ISSUE_REASONS = [
  'not_in_mutation',
  'amount_mismatch',
  'order_cancelled',
  'double_deposit',
  'fraud_indication',
] as const;
export type CodFinanceIssueReason = (typeof COD_FINANCE_ISSUE_REASONS)[number];

export const COD_FINANCE_ISSUE_REASON_LABELS: Record<
  CodFinanceIssueReason,
  string
> = {
  not_in_mutation: 'Belum masuk mutasi',
  amount_mismatch: 'Nominal tidak sesuai',
  order_cancelled: 'Order cancel / retur',
  double_deposit: 'Dobel setor',
  fraud_indication: 'Indikasi fraud',
};

/**
 * What to do about it. Only `return_to_pic` moves the ledger — the other two
 * leave the order exactly where it is, flagged or not.
 */
export const COD_FINANCE_ISSUE_ACTIONS = [
  'hold',
  'return_to_pic',
  'clear',
] as const;
export type CodFinanceIssueAction = (typeof COD_FINANCE_ISSUE_ACTIONS)[number];

export const COD_FINANCE_ISSUE_ACTION_LABELS: Record<
  CodFinanceIssueAction,
  string
> = {
  hold: 'Tahan untuk investigasi',
  return_to_pic: 'Kembalikan ke PIC',
  clear: 'Hapus tanda bermasalah',
};

/** One order inside a deposit batch. */
export interface CodFinanceOrder {
  id: string;
  bookingId: string;
  deliveryUid: string | null;
  clientId: number;
  clientName: string | null;
  hubId: number | null;
  hubName: string | null;
  riderName: string | null;
  riderCode: string | null;
  depositedAmount: number;
  discrepancyAmount: number;
  discrepancyNote: string | null;
  discrepancyTreatment: string | null;
  financeBankRef: string | null;
  financeMatchNote: string | null;
  financeMatchedBy: string | null;
  financeMatchedAt: string | null;
  /** Whole days since the PIC banked it. */
  ageDays: number;
  financeIssueReason: CodFinanceIssueReason | null;
  financeIssueNote: string | null;
  /** When the delivery completed — what the completion filter reads. */
  completedAt: string | null;
  /** Matched and not flagged — the server enforces the same rule on confirm. */
  eligible: boolean;
  /**
   * Outside the active client/hub filter.
   *
   * The row is still here, and the batch totals still count it, because a batch
   * is one bank statement line: showing a filtered subtotal would put a number
   * on screen that appears nowhere in the bank.
   */
  filteredOut: boolean;
}

/** One PIC transfer: one statement line, many orders. Always whole. */
export interface CodFinanceDepositBatch {
  key: string;
  depositedBy: string;
  /** `YYYY-MM-DD` on the Asia/Jakarta clock. */
  depositedOn: string;
  method: string;
  /** Full transfer value — never a filtered subtotal. */
  totalAmount: number;
  totalOrders: number;
  /** How many the active filter would show. Equals `totalOrders` when unfiltered. */
  visibleOrders: number;
  matchedOrders: number;
  flaggedOrders: number;
  clientNames: string[];
  hubNames: string[];
  financeBankRef: string | null;
  orders: CodFinanceOrder[];
}

export interface CodFinanceKpi {
  awaitingAmount: number;
  awaitingOrders: number;
  awaitingDeposits: number;
  matchedOrders: number;
  confirmedAmount: number;
  confirmedOrders: number;
  flaggedOrders: number;
  /**
   * Deposits unverified for a week or more.
   *
   * The one figure that says the PROCESS stopped rather than that the work is
   * hard: every other number moves with volume, this one only grows when the
   * verification pass is not happening.
   */
  staleOrders: number;
  staleAmount: number;
  /** Age of the oldest unverified deposit, in whole days. Null when empty. */
  oldestDepositAgeDays: number | null;
}

export interface CodFinanceBreakdownRow {
  key: string;
  label: string;
  awaitingAmount: number;
  orders: number;
  matchedOrders: number;
  flaggedOrders: number;
}

export interface CodFinanceQueue {
  kpi: CodFinanceKpi;
  breakdownByClient: CodFinanceBreakdownRow[];
  breakdownByHub: CodFinanceBreakdownRow[];
  /** Oldest deposit first, capped server-side — see `totalDeposits`. */
  batches: CodFinanceDepositBatch[];
  /**
   * Deposits matching the scope, ignoring the page cap.
   *
   * Every figure in `kpi` describes the page that came back. When this exceeds
   * `batches.length` the screen has to say so, or it reports a small queue while
   * a large one waits behind the cap.
   */
  totalDeposits: number;
  hasMore: boolean;
}

/**
 * `hubId` value meaning "orders that carry no hub at all".
 *
 * A sentinel rather than `null`, because a query string cannot tell an absent
 * parameter from a null one and those mean opposite things here: absent is "no
 * hub filter", null is "the hub-less ones specifically". Matched by the same
 * constant server-side.
 */
export const COD_NO_HUB = -1;

export interface CodFinanceQueueParams {
  clientId?: number;
  /** A hub id, or `COD_NO_HUB` for the orders with none. */
  hubId?: number;
  /** `week` bounds only the "Confirmed by Finance" tile; the queue is always all. */
  period?: 'week' | 'all';
  /** Completion-date range. Dims orders outside it; never splits a batch. */
  completedStartDate?: string;
  completedEndDate?: string;
}

export const getCodFinanceQueue = async (
  params: CodFinanceQueueParams = {}
): Promise<CodFinanceQueue> => {
  try {
    const res = await Axios.get('/v1/cod-orders/finance', { params });
    return res.data.data as CodFinanceQueue;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/** What the server actually did, and what it deliberately passed over. */
export interface CodFinanceActionResult {
  affected: string[];
  skipped: { shipmentId: string; reason: string }[];
}

/**
 * Tie a set of orders to the bank statement line they arrived on.
 *
 * Flagged orders in the selection are SKIPPED, not refused — matching is a bulk
 * gesture over one statement line, and one problem order should not block the
 * other six. They come back in `skipped`.
 */
export const matchCodDeposit = async (payload: {
  shipmentIds: string[];
  bankRef: string;
  matchNote?: string;
}): Promise<CodFinanceActionResult> => {
  try {
    const res = await Axios.patch('/v1/cod-orders/finance/match', payload);
    return res.data.data as CodFinanceActionResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Confirmed by Finance. TERMINAL — there is no reopen path, and corrections are
 * refused afterwards.
 *
 * ALL-OR-NOTHING on the server: every order sent must be matched, unflagged, and
 * deposited by somebody other than the caller. Send `bulk` when the request came
 * from "Confirm all" across several deposits, which earns the rows a shared
 * audit id distinguishing one decision from many.
 */
export const confirmCodFinance = async (payload: {
  shipmentIds: string[];
  bulk?: boolean;
}): Promise<CodFinanceActionResult> => {
  try {
    const res = await Axios.patch('/v1/cod-orders/finance/confirm', payload);
    return res.data.data as CodFinanceActionResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export interface CodFinanceIssueResult {
  shipmentId: string;
  codStatus: string;
  financeIssueReason: CodFinanceIssueReason | null;
  financeIssueNote: string | null;
  financeReturnedAt: string | null;
}

/** Flag a deposit that does not check out, and route it. Per order. */
export const flagCodFinanceIssue = async (
  shipmentId: string,
  payload: {
    action: CodFinanceIssueAction;
    reason?: CodFinanceIssueReason;
    note?: string;
  }
): Promise<CodFinanceIssueResult> => {
  try {
    const res = await Axios.patch(
      `/v1/cod-orders/${shipmentId}/finance/issue`,
      payload
    );
    return res.data.data as CodFinanceIssueResult;
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Record that a PIC chased the rider about this COD.
 *
 * Fire-and-forget by design at the call site: the follow-up itself is the
 * WhatsApp hand-off, and a failed log must never stop the operator from
 * chasing. Records what we can honestly observe — somebody reached out — not
 * that the message sent or was read, which `wa.me` never tells us.
 */
export const logCodContact = async (
  shipmentId: string,
  payload: { stage?: string | null; note?: string } = {}
): Promise<void> => {
  try {
    await Axios.patch(`/v1/cod-orders/${shipmentId}/contacted`, payload);
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};
